import React, { useState, useEffect } from 'react';
import { ADMIN_API_ENDPOINTS, FRONTEND_URL, SERVER_URL } from '../config/api';
import { getAdminToken } from '../services/apiService';
import Toast from './Toast';
import ConfirmDialog from './ConfirmDialog';

const SparePartsManagement = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPart, setEditingPart] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedModel, setSelectedModel] = useState('all');
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false); // 跟踪图片上传状态
  const [products, setProducts] = useState([]);

  // 获取完整图片URL的辅助函数
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // 如果已经是完整URL,直接返回
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // 如果是相对路径,添加SERVER_URL前缀
    return `${SERVER_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  const partTemplate = {
    model_id: '',
    model_name: { en: '', zh: '' },
    part_id: '',
    part_name: { en: '', zh: '' },
    category: { en: '', zh: '' },
    image: '',
    stock_status: 'In Stock',
    price: 0,
    sort_order: 0,
    status: 'published',
  };

  // 处理图片上传
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      showToast('请选择图片文件', 'error');
      e.target.value = '';
      return;
    }

    // 验证文件大小 (30MB)
    if (file.size > 30 * 1024 * 1024) {
      showToast('图片大小不能超过30MB', 'error');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      setUploadingImage(true);
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.SPARE_PARTS.UPLOAD_IMAGE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // 使用函数式更新确保获取最新状态
        setEditingPart((prev) => ({
          ...prev,
          image: data.imageUrl,
          // 添加时间戳强制重新渲染
          _imageUpdateTimestamp: Date.now(),
        }));
        showToast('图片上传成功', 'success');
      } else {
        showToast(data.message || '上传失败', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('上传失败', 'error');
    } finally {
      setUploadingImage(false);
      // 清空文件输入,允许重新上传相同文件
      e.target.value = '';
    }
  };

  // 显示Toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // 获取零件列表
  const fetchParts = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.SPARE_PARTS.LIST, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setParts(data.parts || []);
      } else {
        showToast('获取零件列表失败', 'error');
      }
    } catch (error) {
      console.error('Fetch parts error:', error);
      showToast('获取零件列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 获取产品列表
  const fetchProducts = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.PRODUCTS.LIST, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  };

  useEffect(() => {
    fetchParts();
    fetchProducts();
  }, []);

  // 获取所有型号：从产品列表生成
  const models = products.map((product) => ({
    id: product.product_id,
    name: product.name?.zh || product.name?.en || product.product_id,
  }));

  const filteredParts =
    selectedModel === 'all'
      ? parts
      : parts.filter((p) => p.model_id === selectedModel);

  // 新建零件
  const handleCreate = () => {
    setEditingPart({ ...partTemplate });
    setShowDialog(true);
  };

  // 编辑零件
  const handleEdit = (part) => {
    setEditingPart({ ...part });
    setShowDialog(true);
  };

  // 处理型号选择变化
  const handleModelChange = (e) => {
    const selectedProductId = e.target.value;
    const selectedProduct = products.find(
      (p) => p.product_id === selectedProductId
    );

    if (selectedProduct) {
      setEditingPart({
        ...editingPart,
        model_id: selectedProduct.product_id,
        model_name: selectedProduct.name,
      });
    }
  };

  // 保存零件
  const handleSave = async () => {
    try {
      const token = getAdminToken();
      const url = editingPart.id
        ? ADMIN_API_ENDPOINTS.SPARE_PARTS.BY_ID(editingPart.id)
        : ADMIN_API_ENDPOINTS.SPARE_PARTS.LIST;

      const method = editingPart.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingPart),
      });

      if (response.ok) {
        setShowDialog(false);
        setEditingPart(null);
        fetchParts();
        showToast(editingPart.id ? '零件已更新' : '零件已创建', 'success');
      } else {
        showToast('保存失败', 'error');
      }
    } catch (error) {
      console.error('Save part error:', error);
      showToast('保存失败', 'error');
    }
  };

  // 删除零件
  const handleDelete = async (id) => {
    setConfirmDialog({
      message: '确定要删除此零件吗？此操作不可撤销。',
      type: 'danger',
      onConfirm: async () => {
        try {
          const token = getAdminToken();
          const response = await fetch(
            ADMIN_API_ENDPOINTS.SPARE_PARTS.BY_ID(id),
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            fetchParts();
            showToast('零件已删除', 'success');
          } else {
            showToast('删除失败', 'error');
          }
        } catch (error) {
          console.error('Delete part error:', error);
          showToast('删除失败', 'error');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  // 切换发布状态
  const handleTogglePublish = async (part) => {
    try {
      const token = getAdminToken();
      const newStatus = part.status === 'published' ? 'draft' : 'published';

      const response = await fetch(
        ADMIN_API_ENDPOINTS.SPARE_PARTS.BY_ID(part.id),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...part, status: newStatus }),
        }
      );

      if (response.ok) {
        fetchParts();
        showToast(
          newStatus === 'published' ? '零件已发布' : '零件已下架',
          'success'
        );
      } else {
        showToast('更新状态失败', 'error');
      }
    } catch (error) {
      console.error('Toggle publish error:', error);
      showToast('更新状态失败', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
          <p className="mt-2 text-gray-400">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">零件支持管理</h2>
        <div className="flex gap-4">
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
          >
            <option value="all">所有型号</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.id} - {model.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleCreate}
            className="px-6 py-2 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded-lg transition-colors"
          >
            + 新建零件
          </button>
        </div>
      </div>

      {/* 零件列表 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#262a22]">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                型号
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                零件编号
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                零件名称
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                分类
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                库存
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                价格
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                状态
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2a2d27]">
            {filteredParts.map((part) => (
              <tr
                key={part.id}
                className="hover:bg-[#262a22] transition-colors"
              >
                <td className="px-4 py-3 text-sm text-white">
                  {part.model_name?.zh || part.model_name?.en || '(无名称)'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                  {part.part_id}
                </td>
                <td className="px-4 py-3 text-sm text-white">
                  {part.part_name?.zh || part.part_name?.en || '(无名称)'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-300">
                  {part.category?.zh || part.category?.en || '(无分类)'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`${part.stock_status === 'In Stock'
                      ? 'text-green-400'
                      : part.stock_status === 'Low Stock'
                        ? 'text-yellow-400'
                        : 'text-red-400'
                      }`}
                  >
                    {part.stock_status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-white font-bold">
                  ${part.price}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${part.status === 'published'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                      }`}
                  >
                    {part.status === 'published' ? '已发布' : '草稿'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(part)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleTogglePublish(part)}
                      className={`px-3 py-1 text-white text-xs rounded transition-colors ${part.status === 'published'
                        ? 'bg-orange-600 hover:bg-orange-700'
                        : 'bg-green-600 hover:bg-green-700'
                        }`}
                    >
                      {part.status === 'published' ? '下架' : '发布'}
                    </button>
                    <button
                      onClick={() => handleDelete(part.id)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredParts.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            暂无零件，点击"新建零件"添加内容
          </div>
        )}
      </div>

      {/* 编辑对话框 */}
      {showDialog && editingPart && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#1a1d17] border-b border-[#2a2d27] px-6 py-4">
              <h3 className="text-xl font-bold text-white">
                {editingPart.id ? '编辑零件' : '新建零件'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* 型号选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  选择型号 *
                </label>
                <select
                  value={editingPart.model_id}
                  onChange={handleModelChange}
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  disabled={editingPart.id} // 编辑时不允许修改型号
                >
                  <option value="">请选择产品型号</option>
                  {products.map((product) => (
                    <option key={product.product_id} value={product.product_id}>
                      {product.product_id} -{' '}
                      {product.name?.zh ||
                        product.name?.en ||
                        product.product_id}
                    </option>
                  ))}
                </select>
                {editingPart.id && (
                  <p className="mt-1 text-xs text-gray-500">
                    注：编辑时不能修改型号ID
                  </p>
                )}
              </div>

              {/* 当前选中的型号信息显示 */}
              {editingPart.model_id && (
                <div className="bg-[#262a22] border border-[#4b5445] rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">
                    当前型号信息
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">ID:</span>
                      <span className="text-white ml-2">
                        {editingPart.model_id}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">中文名称:</span>
                      <span className="text-white ml-2">
                        {editingPart.model_name?.zh || '-'}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">英文名称:</span>
                      <span className="text-white ml-2">
                        {editingPart.model_name?.en || '-'}
                      </span>
                    </div>

                    {/* 零件图片 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        零件图片 - 可选
                      </label>
                      <div className="flex gap-4 items-start">
                        <div className="flex-1">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editingPart.image || ''}
                              onChange={(e) =>
                                setEditingPart({
                                  ...editingPart,
                                  image: e.target.value,
                                })
                              }
                              placeholder="输入图片URL或上传"
                              className="flex-1 px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                            />
                            <label className={`px-4 py-2 ${uploadingImage ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a7f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}>
                              {uploadingImage ? (
                                <>
                                  <svg
                                    className="animate-spin h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  上传中...
                                </>
                              ) : (
                                <>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="currentColor"
                                    viewBox="0 0 256 256"
                                  >
                                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                  </svg>
                                  上传
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                disabled={uploadingImage}
                              />
                            </label>
                          </div>
                          {editingPart.image && (
                            <div className="mt-3 relative w-full h-48 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                              <img
                                src={getImageUrl(editingPart.image)}
                                alt="零件图片预览"
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  e.target.src =
                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              <button
                                onClick={() =>
                                  setEditingPart({
                                    ...editingPart,
                                    image: '',
                                  })
                                }
                                className="absolute top-2 right-2 p-1 bg-red-600/80 text-white rounded-full hover:bg-red-700 transition-colors"
                                title="删除图片"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 256 256"
                                >
                                  <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* 零件编号 */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    零件编号 *
                  </label>
                  <input
                    type="text"
                    value={editingPart.part_id}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        part_id: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                    placeholder="如: TI-001"
                  />
                </div>

                {/* 库存状态 */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    库存状态
                  </label>
                  <select
                    value={editingPart.stock_status}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        stock_status: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  >
                    <option value="In Stock">In Stock</option>
                    <option value="Low Stock">Low Stock</option>
                    <option value="Out of Stock">Out of Stock</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* 零件名称（中文） */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    零件名称（中文） *
                  </label>
                  <input
                    type="text"
                    value={editingPart.part_name?.zh || ''}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        part_name: {
                          ...editingPart.part_name,
                          zh: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                    placeholder="零件中文名称"
                  />
                </div>

                {/* 零件名称（英文） */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    零件名称（英文） *
                  </label>
                  <input
                    type="text"
                    value={editingPart.part_name?.en || ''}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        part_name: {
                          ...editingPart.part_name,
                          en: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                    placeholder="Part name in English"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* 分类（中文） */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    分类（中文） *
                  </label>
                  <input
                    type="text"
                    value={editingPart.category?.zh || ''}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        category: {
                          ...editingPart.category,
                          zh: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                    placeholder="如: 武器系统"
                  />
                </div>

                {/* 分类（英文） */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    分类（英文） *
                  </label>
                  <input
                    type="text"
                    value={editingPart.category?.en || ''}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        category: {
                          ...editingPart.category,
                          en: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                    placeholder="e.g: Weaponry"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* 价格 */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    价格 *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingPart.price}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  />
                </div>

                {/* 排序 */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    排序
                  </label>
                  <input
                    type="number"
                    value={editingPart.sort_order}
                    onChange={(e) =>
                      setEditingPart({
                        ...editingPart,
                        sort_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  />
                </div>

                {/* 状态 */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    状态
                  </label>
                  <select
                    value={editingPart.status}
                    onChange={(e) =>
                      setEditingPart({ ...editingPart, status: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  >
                    <option value="published">已发布</option>
                    <option value="draft">草稿</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-[#1a1d17] border-t border-[#2a2d27] px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setEditingPart(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded-lg transition-colors"
                disabled={
                  !editingPart.model_id ||
                  !editingPart.part_id ||
                  !editingPart.part_name?.zh ||
                  !editingPart.part_name?.en ||
                  !editingPart.category?.zh ||
                  !editingPart.category?.en
                }
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 确认对话框 */}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          type={confirmDialog.type}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

export default SparePartsManagement;
